/**
 * The required "What is this bundle for?" prompt for imported bundles, and a hook that puts it in
 * front of any bundle picker. Imported bundles (Canvas courses, reading-integration bundles) arrive
 * without a purpose; the server marks them `needs_purpose` and refuses to clear their purpose
 * again. This UI asks for the purpose before such a bundle is used for a new activity.
 *
 * @module apps/web/src/components/workspace/BundlePurposeDialog
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { WorkspaceBundleUI } from '@fluxability/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useWorkspaceStore } from '@/stores/workspaceStore';

/** Inputs of the purpose dialog. */
export interface BundlePurposeDialogProps {
  /** Bundle that needs a purpose; null keeps the dialog closed. */
  bundle: WorkspaceBundleUI | null;
  /** Called after the purpose was saved and the workspace reloaded. */
  onSaved: (bundleId: string) => void;
  /** Called when the person closes the dialog without saving. */
  onCancel: () => void;
}

/** Human label for an import source shown in the dialog's explanation. */
function sourceLabel(source: string | null): string {
  if (source === 'canvas') return 'Canvas';
  if (source === 'reading') return 'the reading app';
  return 'an import';
}

/**
 * Required purpose prompt for one imported bundle. Save stays disabled until the field has text;
 * server refusals are shown under the field and keep the dialog open.
 */
export function BundlePurposeDialog({ bundle, onSaved, onCancel }: BundlePurposeDialogProps) {
  const setBundlePurpose = useWorkspaceStore(s => s.setBundlePurpose);
  const [purpose, setPurpose] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bundleId = bundle?.id;

  useEffect(() => {
    setPurpose('');
    setError(null);
  }, [bundleId]);

  async function save() {
    if (!bundle || !purpose.trim()) return;
    setSaving(true);
    const result = await setBundlePurpose(bundle.id, purpose);
    setSaving(false);
    if (result.ok) onSaved(bundle.id);
    else setError(result.error);
  }

  return (
    <Dialog open={Boolean(bundle)} onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>What is {bundle?.name || 'this bundle'} for?</DialogTitle>
          <DialogDescription>
            This bundle came from {sourceLabel(bundle?.importSource ?? null)}. Say why it matters to
            you before you add activities to it. Flux uses the purpose when it plans and explains
            your week.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="bundle-purpose">Purpose (required)</Label>
          <Textarea
            id="bundle-purpose"
            value={purpose}
            required
            autoFocus
            onChange={event => setPurpose(event.target.value)}
            placeholder="For example: pass this course with an A so I can apply to the honors track"
            className="min-h-24"
          />
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Not now
          </Button>
          <Button onClick={save} disabled={saving || !purpose.trim()}>
            Save purpose
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What {@link useBundlePurposeGate} returns to a bundle picker. */
export interface BundlePurposeGate {
  /**
   * Run `onReady` now when the bundle needs no purpose; otherwise open the prompt and run it only
   * after the purpose is saved. Unknown IDs and team bundles pass straight through.
   */
  requirePurpose: (bundleId: string | null | undefined, onReady: () => void) => void;
  /** The prompt element; render it once inside the picker's component. */
  dialog: ReactNode;
}

/**
 * Put the required purpose prompt in front of a bundle choice. Pickers call `requirePurpose` from
 * their change handler and render `dialog`.
 */
export function useBundlePurposeGate(): BundlePurposeGate {
  const getBundle = useWorkspaceStore(s => s.getBundle);
  const [pending, setPending] = useState<{
    /** Bundle being asked about. */
    bundle: WorkspaceBundleUI;
    /** Continuation that runs once the purpose is saved. */
    onReady: () => void;
  } | null>(null);

  const requirePurpose = useCallback(
    (bundleId: string | null | undefined, onReady: () => void) => {
      const bundle = getBundle(bundleId);
      if (bundle?.needsPurpose) setPending({ bundle, onReady });
      else onReady();
    },
    [getBundle]
  );

  const dialog = (
    <BundlePurposeDialog
      bundle={pending?.bundle ?? null}
      onCancel={() => setPending(null)}
      onSaved={() => {
        pending?.onReady();
        setPending(null);
      }}
    />
  );

  return { requirePurpose, dialog };
}
